import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { To, useBeforeUnload } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { faEllipsis, faExclamationCircle } from '@fortawesome/pro-regular-svg-icons';
import Button from '../../../core/components/Button.tsx';
import ConfirmModal from '../../../core/components/ConfirmModal.tsx';
import DropArea from '../../../core/components/DropArea.tsx';
import DropdownMenu from '../../../core/components/DropdownMenu.tsx';
import FormField from '../../../core/components/FormField.tsx';
import { withIcon } from '../../../core/components/Icon.tsx';
import MultiSelectField from '../../../core/components/Fields/MultiSelectField.tsx';
import SegmentedButton from '../../../core/components/SegmentedButton.tsx';
import SelectField from '../../../core/components/Fields/SelectField.tsx';
import { SettingsCard } from '../../../core/components/SettingsFormCard.tsx';
import StringField from '../../../core/components/Fields/StringField.tsx';
import TagsField from '../../../core/components/Fields/TagsField.tsx';
import { useToaster } from '../../../core/components/Toast.tsx';
import { useApplicationContext } from '../../../core/contexts/ApplicationContext.tsx';
import { canConfigureLlmAdvancedSettings } from '../../../users/utils.ts';
import { useGetFieldLabel, useLlmExtractorInputs, useLlmExtractorOutputFieldNames } from '../../llmExtractorFields.ts';
import {
    DELETE_LLM_CONTEXT_FILE,
    DELETE_LLM_EXTRACTOR_CONFIG,
    GET_AVAILABLE_LLM_MODELS,
    GET_LLM_EXTRACTOR_TOOL_OPTIONS,
    UPDATE_LLM_EXTRACTOR_CONFIG,
    UPLOAD_LLM_CONTEXT_FILE,
} from '../../queries.ts';
import FieldsConfig, { FieldsConfigBreadcrumbParent } from '../FieldsConfig.tsx';
import { FieldDefinition } from '../fieldsConfigData.ts';
import ApplyRuleModal from './ApplyRuleModal.tsx';
import RuleFieldsBox, { SuggestionTag } from './RuleFieldsBox.tsx';
import RuleSettingsCard, { RuleSetting } from './RuleSettingsCard.tsx';
import {
    DEFAULT_LLM_MODEL,
    getMissingLookups,
    getRuleDraft,
    getRuleScopeTitle,
    getRuleStatus,
    getRuleTitle,
    NEW_RULE_ID,
    Rule,
    RULE_LEVELS,
    RuleFormValues,
    RuleLevel,
    toRuleFormValues,
    validateRuleClients,
} from './rules.ts';

const EllipsisIcon = withIcon(faEllipsis);
const WarningIcon = withIcon(faExclamationCircle);

const CONTEXT_FILE_ERROR_KEYS: Record<string, string> = {
    CONTEXT_FILE_TOO_LARGE: 'configPage.fineTuning.knowledge.fileTooLarge',
    CONTEXT_FILE_TOO_MANY_TOKENS: 'configPage.fineTuning.knowledge.fileTooManyTokens',
};

const REASONING_EFFORT_OPTIONS = ['none', 'low', 'medium', 'high'] as const;

interface LlmModelOption {
    value: string;
    label: string;
    isReasoning: boolean;
}

interface LlmExtractorTool {
    name: string;
    description: string;
}

interface ContextFileErrorExtensions {
    code?: string;
    fileName?: string;
    tokenCount?: number;
    maxTokens?: number;
    sizeKb?: number;
    maxSizeKb?: number;
}

const SupportOnlyPill = () => {
    const { t } = useTranslation('customer');
    return (
        <span className="rounded px-2 py-0.5 text-xs font-medium text-content-white dark:text-content-black bg-surface-accent-strong-default">
            {t('supportOnlyBadge')}
        </span>
    );
};

type SaveConfirmationReason = 'noField' | 'noLookup' | 'leaveField';

interface SaveConfirmation {
    values: RuleFormValues;
    reason: SaveConfirmationReason;
}

// the field whose page the rule was opened from
interface RuleFormField {
    label: string;
    isWrittenTo: (outputField: string) => boolean;
}

interface RuleFormProps {
    level: RuleLevel;
    channelId: string;
    gqlDocumentType: string;
    channelFields: FieldDefinition[];
    // the rule as saved; null for a rule that isn't saved yet
    savedRule: Rule | null;
    initialValues: RuleFormValues;
    otherRules: Rule[];
    parents: FieldsConfigBreadcrumbParent[];
    title: string;
    subtitle: string;
    // saving asks first when the rule no longer writes to this field, since it then leaves the field's list
    openField?: RuleFormField;
    hasChangesRef: React.RefObject<boolean>;
    onBack: () => void;
    onOpen: (ruleId: string) => void;
    onRefetch: () => Promise<{ data?: { rules?: Rule[] } }>;
    onCopy: () => void;
    getRuleUrl: (channelId: string, ruleId: string) => To;
}

const RuleForm = ({
    level,
    channelId,
    gqlDocumentType,
    channelFields,
    savedRule,
    initialValues,
    otherRules: otherSavedRules,
    parents,
    title,
    subtitle,
    openField = undefined,
    hasChangesRef,
    onBack,
    onOpen,
    onRefetch,
    onCopy,
    getRuleUrl,
}: RuleFormProps) => {
    const { t, i18n } = useTranslation('config');
    const { publishToast } = useToaster();
    const { user } = useApplicationContext();
    const form = useForm<RuleFormValues>({ defaultValues: initialValues, mode: 'onTouched' });
    const [isSaving, setSaving] = useState(false);
    const [isConfirmingDelete, setConfirmingDelete] = useState(false);
    const [confirmation, setConfirmation] = useState<SaveConfirmation | null>(null);
    // settings switched on without a value yet (no file, no tool picked), so their field shows
    const [openSettings, setOpenSettings] = useState<string[]>([]);
    // upload rejections by file name, shown next to the file until it is removed or uploaded
    const [fileUploadErrors, setFileUploadErrors] = useState<Record<string, string>>({});
    // a rule created by a save that failed afterwards is updated by the next save, not created a second time
    const [savedId, setSavedId] = useState(savedRule?.id ?? null);
    // files a save uploaded or deleted before it failed, which the next save skips
    const uploadedFilesRef = useRef(new Set<File>());
    const deletedFileIdsRef = useRef(new Set<string>());
    // the refetch after a failed save lists the rule this form created
    const otherRules = otherSavedRules.filter(({ id }) => id !== savedId);
    const [applyStep, setApplyStep] = useState<'saveFirst' | 'apply' | null>(null);

    const { data: toolsData } = useQuery(GET_LLM_EXTRACTOR_TOOL_OPTIONS);
    const { data: modelsData } = useQuery(GET_AVAILABLE_LLM_MODELS);
    const tools: LlmExtractorTool[] = toolsData?.llmExtractorTools ?? [];
    const models: LlmModelOption[] = modelsData?.availableLlmModels ?? [];
    const inputs = useLlmExtractorInputs(channelId, gqlDocumentType, level);
    const outputs = useLlmExtractorOutputFieldNames(channelId, gqlDocumentType, level);
    const getFieldLabel = useGetFieldLabel();
    const advancedSettingsRef = useRef<HTMLDivElement>(null);

    const { isDirty } = form.formState;
    useEffect(() => {
        hasChangesRef.current = isDirty;
        return () => {
            hasChangesRef.current = false;
        };
    }, [isDirty, hasChangesRef]);
    useBeforeUnload((event) => {
        if (isDirty) event.preventDefault();
    });
    const leave = () => {
        hasChangesRef.current = false;
        onBack();
    };

    const [createRule] = useMutation(RULE_LEVELS[level].createMutation);
    const [updateRule] = useMutation(UPDATE_LLM_EXTRACTOR_CONFIG);
    const [uploadFile] = useMutation(UPLOAD_LLM_CONTEXT_FILE);
    const [deleteFile] = useMutation(DELETE_LLM_CONTEXT_FILE);
    const [deleteRule] = useMutation(DELETE_LLM_EXTRACTOR_CONFIG);

    const isLineItem = level === 'lineItem';
    const canSeeExperimental = canConfigureLlmAdvancedSettings(user);
    const values = form.watch();
    const suggestedLookups = getMissingLookups(channelFields, values.outputFields, values.tools).filter(({ tool }) =>
        tools.some(({ name }) => name === tool)
    );
    const hasOtherDefault = otherRules.some((rule) => rule.isDefault);
    // the backend refuses to turn a default rule that is in use into a client rule
    const isDefaultFixed = !!savedRule?.isDefault && (!!savedRule.fields?.length || !!savedRule.prompt);

    const getContextFileErrorMessage = (extensions: ContextFileErrorExtensions | undefined): string | null => {
        const errorKey = CONTEXT_FILE_ERROR_KEYS[extensions?.code ?? ''];
        if (!errorKey) return null;
        const formatNumber = (value?: number) => value?.toLocaleString(i18n.language);
        return t(errorKey, {
            tokenCount: formatNumber(extensions.tokenCount),
            maxTokens: formatNumber(extensions.maxTokens),
            sizeKb: formatNumber(extensions.sizeKb),
            maxSizeKb: formatNumber(extensions.maxSizeKb),
        });
    };

    const isReasoning = (model: string) => models.find((option) => option.value === model)?.isReasoning ?? true;

    const handleSave = async (formData: RuleFormValues, onSaved?: () => void) => {
        setSaving(true);
        let ruleId = savedId;
        try {
            if (!ruleId) {
                const result = await createRule({
                    variables: { documentType: gqlDocumentType, channelId, isDefault: formData.isDefault },
                });
                ruleId = result.data?.created?.llmExtractorConfig?.id;
                setSavedId(ruleId);
            }

            // the update leaves every value sent as null unchanged, so an empty name or effort is sent as ''; the
            // model only goes along when it changed, so a rule on the backend's default model isn't pinned to it
            const modelBefore = savedRule ? initialValues.model : DEFAULT_LLM_MODEL;
            const experimentalVariables = canSeeExperimental
                ? {
                      model: formData.model !== modelBefore ? formData.model : null,
                      reasoningEffort: isReasoning(formData.model) ? formData.reasoningEffort : '',
                      testingOnly: formData.testingOnly,
                      ...(isLineItem && { lineItemOperationsMode: formData.lineItemOperationsMode }),
                  }
                : {};
            await updateRule({
                variables: {
                    id: ruleId,
                    name: formData.name ?? '',
                    fields: formData.outputFields,
                    prompt: formData.prompt,
                    documentInput: formData.inputFields,
                    tools: formData.tools,
                    useRag: formData.useRag,
                    isEnabled: formData.isEnabled,
                    isDefault: formData.isDefault,
                    partitionIds: formData.isDefault ? [] : formData.partitionIds,
                    ...experimentalVariables,
                },
            });

            const newFiles = formData.files.filter(
                (file): file is File => file instanceof File && !uploadedFilesRef.current.has(file)
            );
            if (newFiles.length > 0) {
                await uploadFile({ variables: { input: { llmExtractorConfigId: ruleId, files: newFiles } } });
                newFiles.forEach((file) => uploadedFilesRef.current.add(file));
            }
            setFileUploadErrors({});

            const keptFileIds = new Set(formData.files.flatMap((file) => (file instanceof File ? [] : [file.id])));
            const filesToDelete =
                savedRule?.contextFiles?.filter(
                    (file) => !keptFileIds.has(file.id) && !deletedFileIdsRef.current.has(file.id)
                ) ?? [];
            await Promise.all(
                filesToDelete.map(async (file) => {
                    await deleteFile({ variables: { id: file.id } });
                    deletedFileIdsRef.current.add(file.id);
                })
            );

            publishToast({
                description: savedRule
                    ? t('configPage.fineTuning.saveSuccess')
                    : t('configPage.fineTuning.createSuccess'),
                status: 'success',
            });
        } catch (e) {
            console.error(e);
            const extensions: ContextFileErrorExtensions | undefined = e?.graphQLErrors?.[0]?.extensions;
            const fileErrorMessage = getContextFileErrorMessage(extensions);
            if (fileErrorMessage && extensions?.fileName) {
                setFileUploadErrors((errors) => ({ ...errors, [extensions.fileName]: fileErrorMessage }));
            }
            // the server's reason (e.g. a client number in another rule) goes under the message
            publishToast(
                fileErrorMessage
                    ? { description: fileErrorMessage, status: 'error' }
                    : {
                          title: t('configPage.fineTuning.saveError'),
                          description: e instanceof Error ? e.message : '',
                          status: 'error',
                      }
            );
            // a rule this form created before the save failed is on the backend, so the list shows it
            if (ruleId && !savedRule) onRefetch();
            setSaving(false);
            return;
        }
        const refetched = await onRefetch();
        const saved = refetched.data?.rules?.find(({ id }) => id === ruleId);
        if (saved) form.reset(toRuleFormValues(saved));
        hasChangesRef.current = false;
        setSaving(false);
        if (!savedRule) onOpen(ruleId);
        onSaved?.();
    };

    // an action, not an edit: a saved rule is (de)activated right away
    const toggleActive = async () => {
        const isEnabled = !form.getValues('isEnabled');
        if (!savedRule) {
            form.setValue('isEnabled', isEnabled);
            return;
        }
        form.resetField('isEnabled', { defaultValue: isEnabled });
        try {
            await updateRule({ variables: { id: savedRule.id, isEnabled } });
            publishToast({
                description: isEnabled
                    ? t('configPage.fineTuning.activateSuccess')
                    : t('configPage.fineTuning.deactivateSuccess'),
                status: 'success',
            });
            onRefetch();
        } catch (e) {
            console.error(e);
            form.resetField('isEnabled', { defaultValue: !isEnabled });
            publishToast({ description: t('configPage.fineTuning.saveError'), status: 'error' });
        }
    };

    const handleDelete = async () => {
        if (!savedRule) return;
        try {
            await deleteRule({ variables: { id: savedRule.id } });
        } catch (e) {
            console.error(e);
            publishToast({ description: t('configPage.fineTuning.deleteError'), status: 'error' });
            setConfirmingDelete(false);
            return;
        }
        publishToast({ description: t('configPage.fineTuning.deleteSuccess'), status: 'success' });
        await onRefetch();
        leave();
    };

    const toggleSetting = (key: string, isOn: boolean, clear: () => void) => {
        setOpenSettings(isOn ? [...openSettings, key] : openSettings.filter((item) => item !== key));
        if (!isOn) clear();
    };

    const settings: RuleSetting[] = [
        {
            key: 'name',
            label: t('configPage.fineTuning.settings.name'),
            help: t('configPage.fineTuning.settings.nameHelp', { title: getRuleScopeTitle(values, t) }),
            isActive: values.name !== null,
            onToggle: (isOn) => form.setValue('name', isOn ? '' : null, { shouldDirty: true }),
            field: (
                <StringField
                    value={values.name ?? ''}
                    onValueChange={(value) => form.setValue('name', value ?? '', { shouldDirty: true })}
                    placeholder={t('configPage.fineTuning.settings.namePlaceholder')}
                />
            ),
        },
        {
            key: 'files',
            label: t('configPage.fineTuning.settings.knowledge'),
            help: t('configPage.fineTuning.settings.knowledgeHelp'),
            isActive: values.files.length > 0 || openSettings.includes('files'),
            onToggle: (isOn) => toggleSetting('files', isOn, () => form.setValue('files', [], { shouldDirty: true })),
            field: (
                <DropArea
                    placeholder={t('configPage.fineTuning.knowledge.placeholder')}
                    accept={{
                        'text/plain': ['.txt'],
                        'text/csv': ['.csv'],
                        'text/markdown': ['.md'],
                        'application/json': ['.json'],
                    }}
                    onFilesChange={(files) => {
                        const fileNames = new Set(files.map((file) => file.name));
                        setFileUploadErrors((errors) =>
                            Object.fromEntries(Object.entries(errors).filter(([name]) => fileNames.has(name)))
                        );
                        form.setValue('files', files, { shouldDirty: true });
                    }}
                    files={values.files}
                    renderFileDetails={(file) => {
                        const uploadError = !file.id && fileUploadErrors[file.name];
                        if (uploadError) {
                            return (
                                <span className="inline-flex items-center gap-1 text-content-danger-strong">
                                    <WarningIcon />
                                    {uploadError}
                                </span>
                            );
                        }
                        return (
                            file.tokenCount != null &&
                            t('configPage.fineTuning.knowledge.tokenCount', {
                                tokenCount: file.tokenCount.toLocaleString(i18n.language),
                            })
                        );
                    }}
                />
            ),
        },
        {
            key: 'tools',
            label: t('configPage.fineTuning.settings.tools'),
            help: (
                <>
                    {t('configPage.fineTuning.settings.toolsHelp')}
                    {suggestedLookups.length > 0 && (
                        <span className="mt-1.5 flex flex-wrap gap-1">
                            {suggestedLookups.map(({ tool }) => (
                                <SuggestionTag
                                    key={tool}
                                    label={tool}
                                    onClick={() =>
                                        form.setValue('tools', [...values.tools, tool], { shouldDirty: true })
                                    }
                                />
                            ))}
                        </span>
                    )}
                </>
            ),
            isActive: values.tools.length > 0 || openSettings.includes('tools'),
            isAlwaysShown: suggestedLookups.length > 0,
            onToggle: (isOn) => toggleSetting('tools', isOn, () => form.setValue('tools', [], { shouldDirty: true })),
            field: (
                <MultiSelectField
                    options={tools.map((tool) => ({
                        value: tool.name,
                        label: tool.name,
                        description: tool.description,
                    }))}
                    value={values.tools}
                    onValueChange={(value) => form.setValue('tools', value, { shouldDirty: true })}
                    placeholder={t('configPage.fineTuning.settings.toolsPlaceholder')}
                />
            ),
        },
        {
            key: 'useRag',
            label: t('configPage.fineTuning.settings.learnFromCorrections'),
            help: isLineItem
                ? t('configPage.fineTuning.settings.learnFromCorrectionsLineItemHelp')
                : t('configPage.fineTuning.settings.learnFromCorrectionsHelp'),
            isActive: values.useRag,
            onToggle: (isOn) => form.setValue('useRag', isOn, { shouldDirty: true }),
        },
    ];

    const isReasoningModel = isReasoning(values.model);
    const experimentalSettings: RuleSetting[] = [
        {
            key: 'model',
            label: t('configPage.fineTuning.settings.modelSettings'),
            help: t('configPage.fineTuning.settings.modelSettingsHelp'),
            // off is the defaults: the default model with the model's own reasoning effort
            isActive: values.model !== DEFAULT_LLM_MODEL || !!values.reasoningEffort || openSettings.includes('model'),
            onToggle: (isOn) =>
                toggleSetting('model', isOn, () => {
                    form.setValue('model', DEFAULT_LLM_MODEL, { shouldDirty: true });
                    form.setValue('reasoningEffort', '', { shouldDirty: true });
                }),
            field: (
                <div className="grid grid-cols-2 gap-4">
                    <div className="flex flex-col gap-1">
                        <span className="text-xs text-content-medium">{t('configPage.fineTuning.settings.model')}</span>
                        <SelectField
                            options={models.map((model) => ({ label: model.label, value: model.value }))}
                            // the backend always keeps a model, so it can be changed but not cleared
                            required
                            value={values.model}
                            onValueChange={(value) =>
                                form.setValue('model', value ?? DEFAULT_LLM_MODEL, { shouldDirty: true })
                            }
                        />
                    </div>
                    <div className="flex flex-col gap-1">
                        <span className="text-xs text-content-medium">
                            {t('configPage.fineTuning.settings.reasoningEffort')}
                        </span>
                        <SelectField
                            options={REASONING_EFFORT_OPTIONS.map((option) => ({
                                label: t(`configPage.fineTuning.settings.reasoningEfforts.${option}`),
                                value: option,
                            }))}
                            value={(isReasoningModel && values.reasoningEffort) || null}
                            onValueChange={(value) =>
                                form.setValue('reasoningEffort', value ?? '', { shouldDirty: true })
                            }
                            disabled={!isReasoningModel}
                            placeholder={t('configPage.fineTuning.settings.reasoningEffortPlaceholder')}
                        />
                    </div>
                </div>
            ),
        },
        ...(isLineItem
            ? [
                  {
                      key: 'lineItemOperationsMode',
                      label: t('configPage.fineTuning.settings.lineItemOperations'),
                      help: t('configPage.fineTuning.settings.lineItemOperationsHelp'),
                      isActive: values.lineItemOperationsMode,
                      onToggle: (isOn: boolean) => form.setValue('lineItemOperationsMode', isOn, { shouldDirty: true }),
                  },
              ]
            : []),
        {
            key: 'testingOnly',
            label: t('configPage.fineTuning.settings.testDocumentsOnly'),
            help: t('configPage.fineTuning.settings.testDocumentsOnlyHelp'),
            isActive: values.testingOnly,
            onToggle: (isOn) => form.setValue('testingOnly', isOn, { shouldDirty: true }),
        },
    ];

    const clientRuleHelp = hasOtherDefault
        ? t('configPage.fineTuning.runsFor.selectedHelpDefaultExists')
        : t('configPage.fineTuning.runsFor.selectedHelp');
    const defaultRuleHelp = isDefaultFixed
        ? `${t('configPage.fineTuning.runsFor.defaultHelp')} ${t('configPage.fineTuning.runsFor.defaultFixedHelp')}`
        : t('configPage.fineTuning.runsFor.defaultHelp');

    const confirmOrSave = (formData: RuleFormValues, isLookupConfirmed = false) => {
        // extraction skips a rule without fields, except one that adds, removes and splits line items
        if (!formData.outputFields.length && !(isLineItem && formData.lineItemOperationsMode)) {
            setConfirmation({ values: formData, reason: 'noField' });
        } else if (!isLookupConfirmed && suggestedLookups.length) {
            setConfirmation({ values: formData, reason: 'noLookup' });
        } else if (openField && !formData.outputFields.some(openField.isWrittenTo)) {
            setConfirmation({ values: formData, reason: 'leaveField' });
        } else {
            handleSave(formData);
        }
    };

    const submit = form.handleSubmit((formData) => confirmOrSave(formData));

    const confirmationTitles: Record<SaveConfirmationReason, string> = {
        noField: t('configPage.fineTuning.noFieldTitle'),
        noLookup: t('configPage.fineTuning.noLookupTitle'),
        leaveField: t('configPage.fineTuning.field.leaveTitle', { field: openField?.label }),
    };

    const actions = (
        <DropdownMenu>
            <DropdownMenu.Trigger asChild>
                <Button type="button" variant="ghost" aria-label={t('configPage.fineTuning.moreActions')}>
                    <EllipsisIcon />
                </Button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Content align="end" className="min-w-48">
                <DropdownMenu.Item onSelect={toggleActive}>
                    {values.isEnabled ? t('configPage.fineTuning.deactivate') : t('configPage.fineTuning.activate')}
                </DropdownMenu.Item>
                {savedRule && (
                    <>
                        <DropdownMenu.Item onSelect={onCopy}>{t('configPage.fineTuning.copy')}</DropdownMenu.Item>
                        <DropdownMenu.Item onSelect={() => setApplyStep(isDirty ? 'saveFirst' : 'apply')}>
                            {t('configPage.fineTuning.apply.action')}
                        </DropdownMenu.Item>
                        <DropdownMenu.Separator />
                        <DropdownMenu.Item danger onSelect={() => setConfirmingDelete(true)}>
                            {t('configPage.fineTuning.delete')}
                        </DropdownMenu.Item>
                    </>
                )}
            </DropdownMenu.Content>
        </DropdownMenu>
    );

    return (
        <FieldsConfig.Page parents={parents} current={title} title={title} subtitle={subtitle} actions={actions}>
            <form onSubmit={submit} className="flex flex-col gap-5">
                <FieldsConfig.Section title={t('configPage.fineTuning.runsFor.label')}>
                    <div className="flex flex-col gap-2">
                        <SegmentedButton
                            variant="joined"
                            value={values.isDefault ? 'all' : 'selected'}
                            onValueChange={(value) =>
                                form.setValue('isDefault', value === 'all', { shouldDirty: true })
                            }
                        >
                            <SegmentedButton.Item value="all" disabled={hasOtherDefault && !values.isDefault}>
                                {t('configPage.fineTuning.allClients')}
                            </SegmentedButton.Item>
                            <SegmentedButton.Item value="selected" disabled={isDefaultFixed}>
                                {t('configPage.fineTuning.runsFor.selectedClients')}
                            </SegmentedButton.Item>
                        </SegmentedButton>
                        {values.isDefault ? (
                            <span className="text-xs text-content-medium">{defaultRuleHelp}</span>
                        ) : (
                            <Controller
                                name="partitionIds"
                                control={form.control}
                                rules={{ validate: (partitionIds) => validateRuleClients(partitionIds, otherRules, t) }}
                                render={({ field, fieldState }) => (
                                    <FormField error={fieldState.error} helpText={clientRuleHelp}>
                                        <TagsField
                                            value={field.value}
                                            onValueChange={field.onChange}
                                            onBlur={field.onBlur}
                                            placeholder={t('configPage.fineTuning.runsFor.clientsPlaceholder')}
                                        />
                                    </FormField>
                                )}
                            />
                        )}
                    </div>
                </FieldsConfig.Section>
                <FieldsConfig.Section title={t('configPage.fineTuning.instructions')}>
                    <RuleFieldsBox form={form} inputs={inputs} outputs={outputs} gqlDocumentType={gqlDocumentType} />
                </FieldsConfig.Section>
                <div ref={advancedSettingsRef}>
                    <RuleSettingsCard title={t('configPage.fineTuning.settings.advanced')} settings={settings} />
                </div>
                {canSeeExperimental && (
                    <RuleSettingsCard
                        title={t('configPage.fineTuning.settings.experimental')}
                        settings={experimentalSettings}
                        badge={<SupportOnlyPill />}
                    />
                )}
                <FieldsConfig.SaveBar onCancel={leave} onSave={() => submit()} isSaving={isSaving} />
            </form>
            {isConfirmingDelete && savedRule && (
                <ConfirmModal
                    title={t('configPage.fineTuning.deleteTitle', { title: getRuleTitle(savedRule, t) })}
                    labelConfirm={t('configPage.fineTuning.deleteRule')}
                    danger
                    onConfirm={handleDelete}
                    onCancel={() => setConfirmingDelete(false)}
                >
                    <p>{t('configPage.fineTuning.deleteConfirm')}</p>
                </ConfirmModal>
            )}
            {applyStep === 'saveFirst' && (
                <ConfirmModal
                    title={t('configPage.fineTuning.apply.saveFirstTitle')}
                    labelConfirm={t('configPage.fineTuning.apply.saveAndContinue')}
                    onConfirm={() => {
                        setApplyStep(null);
                        form.handleSubmit((formData) => handleSave(formData, () => setApplyStep('apply')))();
                    }}
                    onCancel={() => setApplyStep(null)}
                >
                    <p>{t('configPage.fineTuning.apply.saveFirstMessage')}</p>
                </ConfirmModal>
            )}
            {applyStep === 'apply' && savedRule && (
                <ApplyRuleModal
                    level={level}
                    rule={savedRule}
                    channelId={channelId}
                    gqlDocumentType={gqlDocumentType}
                    getRuleUrl={getRuleUrl}
                    onClose={() => setApplyStep(null)}
                />
            )}
            {confirmation && (
                <ConfirmModal
                    title={confirmationTitles[confirmation.reason]}
                    labelConfirm={t('configPage.fineTuning.saveRule')}
                    onConfirm={() => {
                        setConfirmation(null);
                        if (confirmation.reason === 'noLookup') {
                            confirmOrSave(confirmation.values, true);
                        } else {
                            handleSave(confirmation.values);
                        }
                    }}
                    onCancel={() => {
                        setConfirmation(null);
                        if (confirmation.reason === 'noLookup') {
                            advancedSettingsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }
                    }}
                >
                    <div className="flex flex-col gap-2">
                        {confirmation.reason === 'noField' && <p>{t('configPage.fineTuning.noFieldMessage')}</p>}
                        {confirmation.reason === 'noLookup' && (
                            <p>
                                {t('configPage.fineTuning.noLookupMessage', {
                                    fields: new Intl.ListFormat(i18n.language, { type: 'conjunction' }).format(
                                        suggestedLookups.flatMap(({ fields }) => fields).map(getFieldLabel)
                                    ),
                                })}
                            </p>
                        )}
                        {confirmation.reason !== 'noLookup' && openField && (
                            <p>{t('configPage.fineTuning.field.leaveMessage', { field: openField.label })}</p>
                        )}
                    </div>
                </ConfirmModal>
            )}
        </FieldsConfig.Page>
    );
};

interface RuleEditorProps
    extends Omit<RuleFormProps, 'savedRule' | 'initialValues' | 'otherRules' | 'title' | 'subtitle'> {
    // the rules of the level
    rules: Rule[];
    // a saved rule's id, or NEW_RULE_ID for a draft
    ruleId: string;
    // the saved rule a draft copies
    copyOf?: string | null;
    // the output fields a new rule starts with
    draftFields?: string[];
}

// a rule on its own page, saved or a draft; the options of its level are only loaded once a rule is open
const RuleEditor = ({ rules, ruleId, copyOf = null, draftFields = [], ...formProps }: RuleEditorProps) => {
    const { t } = useTranslation('config');
    const savedRule = rules.find(({ id }) => id === ruleId) ?? null;
    const draft = getRuleDraft(
        formProps.level,
        rules,
        rules.find(({ id }) => id === copyOf),
        t,
        draftFields
    );
    const title = savedRule ? getRuleTitle(savedRule, t) : draft.title;
    const subtitle = savedRule ? getRuleStatus(savedRule, t) : t('configPage.notSavedYet');

    return (
        <RuleForm
            // the output fields load after a page reload on a new rule's URL; the form takes its values on mount
            key={savedRule ? savedRule.id : `${copyOf ?? NEW_RULE_ID}:${formProps.level}:${draftFields.join()}`}
            {...formProps}
            title={title}
            subtitle={subtitle}
            savedRule={savedRule}
            initialValues={savedRule ? toRuleFormValues(savedRule) : draft.values}
            otherRules={rules.filter(({ id }) => id !== ruleId)}
        />
    );
};

export default RuleEditor;
